<template>
	<div v-if="showModal" class="c-overlay" @click="closeModal"></div>

	<!-- STORE FILTERS BY INDUSTRY -->
	<div class="c-store-filters">

		<div class="c-store-filters__layout">
			<button v-for="industry in allIndustries"
					:key="industry.key"
					:class="['c-store-filter', { 'is-selected': selectedIndustry === industry.key }]"
					:data-industry="industry.key"
					@click="toggleIndustryFilter(industry.key)">
				{{ industry.name }}
			</button>

			<a href="/tech-support/" class="c-store-filter">Tech Support</a>
		</div>

	</div>

	<!-- MOBILE - TOGGLE VIEW BETWEEN MAP AND LOCATION LISTING -->
	<div class="c-store-view-toggle">
		<button type="button"
				:class="['c-store-view-toggler', { 'is-active': activeView === 'map' }]"
				@click="setActiveView('map')">
			<span class="iconf-address"></span>View map
		</button>
		<button type="button"
				:class="['c-store-view-toggler', { 'is-active': activeView === 'list' }]"
				@click="setActiveView('list')">
			<span class="iconf-view-list"></span>View Store list
		</button>
	</div>

	<!-- STORE DETAILS MODAL -->
	<div class="c-store-details-modal" v-if="showModal">
		<header class="c-store-details-modal__header">
			<h3 class="c-store-details-modal__name">{{selectedItem.name}}</h3>
			<button class="c-store-details-modal__close" type="button" aria-label="Close popup" @click="closeModal"><span class="iconf-close"></span></button>
		</header>
		<div class="c-store-details-modal__details">
			<h4 class="c-store-details-modal__heading">Branch detail</h4>
			<address v-if="selectedItem.address" class="c-store-details-modal__address">
				<span class="iconf-address c-store-details-modal__icon"></span>{{selectedItem.address}}
				<a class="c-store-details-modal__address-direction"
				   :href="getDirectionsUrl(selectedItem)"
				   target="_blank"
				   rel="noopener">
					Get directions
				</a>
			</address>
			<p v-if="selectedItem.postal" class="c-store-details-modal__postal"><span class="iconf-address c-store-details-modal__icon"></span>{{selectedItem.postal}}</p>
			<p v-if="selectedItem.hours" class="c-store-details-modal__hours"><span class="iconf-clock c-store-details-modal__icon"></span>{{selectedItem.hours}}</p>
			<a v-if="selectedItem.phone" class="c-store-details-modal__phone" :href="'tel:' + selectedItem.phone"><span class="iconf-phone c-store-details-modal__icon"></span>{{selectedItem.phone}}</a>
		</div>
		<footer class="c-store-details-modal__footer">
			<div v-if="selectedItem.email">
				<h4 class="c-store-details-modal__heading">Get a quote</h4>
				<a class="c-store-details-modal__email" :href="'mailto:' + selectedItem.email"><span class="iconf-email c-store-details-modal__icon"></span>{{selectedItem.email}}</a>
			</div>
			<div v-if="selectedItem.creditFormUrl">
				<h4 class="c-store-details-modal__heading">Apply for a credit</h4>
				<a class="c-store-details-modal__apply" :href="selectedItem.creditFormUrl" target="_blank" rel="noreferrer nofollow"><span class="iconf-download c-store-details-modal__icon"></span>Apply for a credit</a>
			</div>
		</footer>
	</div>

	<!-- STORES MAP -->
	<div class="c-store-map">
		<div class="c-store-map__layout" v-show="isMobile ? activeView === 'map' : true">
			<GoogleMap :api-key="APIKey"
					   :map-id="MapID || undefined"
					   style="width: 100%; height: 100%"
					   :center="mapCenter"
					   :zoom="mapZoom"
					   @idle="onMapIdle">
				<MarkerCluster v-if="isMapReady" :styles="clusterStyles">
					<CustomMarker v-for="(location, index) in filteredList"
								  :key="index"
								  :options="{ position: location.position }"
								  @click="markerPosition(location), openModal(location)"
								  :ref="setMarkerRef(index)">
						<div :class="['c-store-map__marker', { 'is-hovered': hoveredListIndex === index }]"
							 @mouseover="onMarkerMouseOver(location, index)"
							 @mouseleave="onMarkerMouseOut(location, index)">
							<svg width="28" height="38" viewBox="0 0 28 38" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d="M14 0C10.2885 0.00501134 6.73039 1.50796 4.10594 4.17926C1.4815 6.85057 0.00492342 10.4722 0 14.25C0 24.4803 13.0433 37.2934 13.5975 37.8337C13.7053 37.9404 13.8497 38 14 38C14.1503 38 14.2947 37.9404 14.4025 37.8337C14.9567 37.2934 28 24.4803 28 14.25C27.9951 10.4722 26.5185 6.85057 23.8941 4.17926C21.2696 1.50796 17.7115 0.00501134 14 0Z" :fill="clusterFillColour" />
							</svg>
						</div>
					</CustomMarker>
				</MarkerCluster>
			</GoogleMap>
		</div>

		<!-- STORE LISTING -->
		<div class="c-store-locations__list c-store-locations-list" v-show="isMobile ? activeView === 'list' : true">
			<div class="c-store-locations-list__search">
				<h3 for="branch-search" class="c-store-locations-list__search-heading"><span class="iconf-address"></span>Find a branch</h3>
				<div class="c-store-locations-list__search-box">
					<form>
						<input id="branch-search" class="c-store-locations-list__search-input" type="text" v-model="searchText" placeholder="Search locations" />
						<button v-if="searchText"
								type="button"
								class="c-store-locations-list__search-reset"
								@click="resetSearch">
							<span class="iconf-close"></span>
						</button>
					</form>
				</div>
				<p class="c-store-locations-list__search-count" v-if="searchText || selectedIndustry">{{this.filteredList.length}} branches found.</p>
			</div>
			<div class="c-store-locations-listing">
				<div class="c-store-locations-listing__item"
					 v-for="(location, index) in filteredList"
					 :key="index"
					 @mouseover="onListItemMouseOver(index)"
					 @mouseleave="onListItemMouseOut">
					<div class="c-store-locations-listing__item-name">
						{{ location.name }}
					</div>
					<address v-if="location.address" class="c-store-locations-listing__item-address">
						<span class="iconf-address"></span>&nbsp;{{ location.address }}
					</address>
					<a v-if="location.phone" class="c-store-locations-listing__item-phone" href="tel:{{ location.phone }}">
						<span class="iconf-phone"></span>&nbsp;{{ location.phone }}
					</a>

					<div class="c-store-locations-listing__item-ctas">
						<a class="c-store-locations-listing__item-direction"
						   :href="getDirectionsUrl(location)"
						   target="_blank"
						   rel="noopener">
							<span class="iconf-arrow-right"></span>
							Get directions
						</a>
						<button class="c-store-locations-listing__item-details" @click="markerPosition(location), openModal(location)">See details</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>


<script>
	import { ref } from "vue";
	import { GoogleMap, CustomMarker, MarkerCluster } from "vue3-google-map";
	import locations from 'Vue/store-locations';

	export default {
		name: "mapComponent",
		components: {
			GoogleMap,
			CustomMarker,
			MarkerCluster
		},
		data() {
			const defaultMapCenter = { lat: -41, lng: 174 };
			const defaultMapZoom = 6;
			const mapCenter = ref({ ...defaultMapCenter });
			const mapZoom = ref(defaultMapZoom);

			const siteMapSettings = {
				"site-metalcraft": {
					mapId: "c09e15a69acd60bc9e436520",
					clusterFillColour: "#FFA000"
				},
                "site-reofab": {
                    mapId: "c09e15a69acd60bc705b72e8",
					clusterFillColour: "#000000"
                },
				"site-akaranatimbers": {
					mapId: "c09e15a69acd60bcf51ed92f",
					clusterFillColour: "#fed500"
				}
			};
			const defaultSiteSettings = siteMapSettings["site-metalcraft"];
			const matchedSiteClass = Object.keys(siteMapSettings).find(siteClass => document.body.classList.contains(siteClass));
			const activeSiteSettings = matchedSiteClass ? siteMapSettings[matchedSiteClass] : defaultSiteSettings;

			const APIKey = "AIzaSyDlcRXfGgblv-S9dqrSA1ndZ0a8BS_IoAU";
			const MapID = activeSiteSettings.mapId ?? "";
			const clusterFillColour = activeSiteSettings.clusterFillColour;

			return {
				locations,
				mapCenter,
				mapZoom,
				defaultMapCenter,
				defaultMapZoom,
				APIKey,
				MapID,
				clusterFillColour,
				searchText: '',
				selectedItem: '',
				showModal: false,
				activeView: 'list',
				selectedIndustry: null,
				hoveredListIndex: null,
				markerRefs: [],
				allIndustries: [],
				isMapReady: false,
				clusterStyles: [
					{
						url: 'data:image/svg+xml;base64,' + btoa(`
							<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
								<circle cx="20" cy="20" r="20" fill="${clusterFillColour}" opacity="0.8"/>
								<circle cx="20" cy="20" r="15" fill="${clusterFillColour}"/>
							</svg>
						`),
						height: 40,
						width: 40,
						textColor: '#233747',
						textSize: 12,
						fontWeight: 'bold',
						fontFamily: 'Arial, sans-serif',
						anchorText: [0, 0],
						anchorIcon: [20, 20]
					},
					{
						url: 'data:image/svg+xml;base64,' + btoa(`
							<svg width="50" height="50" viewBox="0 0 50 50" xmlns="http://www.w3.org/2000/svg">
								<circle cx="25" cy="25" r="25" fill="${clusterFillColour}" opacity="0.8"/>
								<circle cx="25" cy="25" r="20" fill="${clusterFillColour}"/>
							</svg>
						`),
						height: 50,
						width: 50,
						textColor: '#233747',
						textSize: 14,
						fontWeight: 'bold',
						fontFamily: 'Arial, sans-serif',
						anchorText: [0, 0],
						anchorIcon: [25, 25]
					},
					{
						url: 'data:image/svg+xml;base64,' + btoa(`
							<svg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg">
								<circle cx="30" cy="30" r="30" fill="${clusterFillColour}" opacity="0.8"/>
								<circle cx="30" cy="30" r="25" fill="${clusterFillColour}"/>
							</svg>
						`),
						height: 60,
						width: 60,
						textColor: '#233747',
						textSize: 16,
						fontWeight: 'bold',
						fontFamily: 'Arial, sans-serif',
						anchorText: [0, 0],
						anchorIcon: [30, 30]
					}
				]
			};
		},
		watch: {
			searchText(newVal) {
				if (newVal === '') {
					this.mapCenter = { ...this.defaultMapCenter };
					this.mapZoom = this.defaultMapZoom;
				}
			}
		},
		computed: {
			isMobile() {
				return window.innerWidth < 1024;
			},
			filteredList() {
				let filteredlocations = Array.from(this.locations).filter(location =>
					Number.isFinite(location?.position?.lat) && Number.isFinite(location?.position?.lng)
				);

				// Filter by search text
				if (this.searchText) {
					const searchTerm = this.searchText.toLowerCase();
					filteredlocations = filteredlocations.filter(item => {
						return Object.values(item).some(value => {
							if (typeof value === 'string') {
								return value.toLowerCase().includes(searchTerm);
							} else if (Array.isArray(value)) {
								var stringValue = value.join(' ').toLowerCase();
								return stringValue.includes(searchTerm);
							}
							return false;
						});
					});
				}

				// Filter by selected industry
				if (this.selectedIndustry) {

					filteredlocations = filteredlocations.filter(location =>
						location.industry?.some(industry => industry.key === this.selectedIndustry)
					);
				}

				return filteredlocations;
			}
		},
		mounted() {
			// Fetch the stores details data from the API
			fetch(locations.api).then(res => res.json()).then(response => {
				this.locations = response.locations || [];
				this.allIndustries = response.allIndustries || [];
			}).catch(() => {
				this.locations = [];
			});

			window.addEventListener('resize', this.handleResize);
		},
		beforeUnmount() {
			window.removeEventListener('resize', this.handleResize);
		},
		methods: {
			onMapIdle() {
				this.isMapReady = true;
			},
			setActiveView(view) {
				this.activeView = view;
			},
			openModal(item) {
				this.selectedItem = item;
				this.showModal = true;

				// console.log(this.mapZoom);
				// console.log(this.selectedItem);
			},
			closeModal() {
				this.showModal = false;
				this.selectedItem = null; // Optional: clear selected item data on close
			},
			markerPosition(marker) {
				this.mapCenter = { ...marker.position };
				this.mapZoom = 10;
			},
			handleResize() {
				this.$forceUpdate(); // triggers recompute of isMobile
			},
			getDirectionsUrl(location) {
				// Generate google map direction link
				if (location.position && location.position.lat && location.position.lng) {
					return `https://www.google.com/maps/dir/?api=1&destination=${location.position.lat},${location.position.lng}`;
				}
				return '#';
			},
			toggleIndustryFilter(industry) {
				// filter stores by its industry
				if (this.selectedIndustry === industry) {
					this.selectedIndustry = null; // Unselect if already selected
				} else {
					this.selectedIndustry = industry;
				}
			},
			onMarkerMouseOver(location, index) {
				// scroll item to the top of the listing when marker is hovered
				this.hoveredListIndex = index;
				const items = document.querySelectorAll('.c-store-locations-listing__item');
				items.forEach(item => item.classList.remove('is-hovered'));
				const target = items[index];
				if (target) {
					target.classList.add('is-hovered');
					const container = target.closest('.c-store-locations-listing');
					if (container) {
						const containerRect = container.getBoundingClientRect();
						const targetRect = target.getBoundingClientRect();
						const offset = targetRect.top - containerRect.top + container.scrollTop;
						container.scrollTo({
							top: offset,
							behavior: 'smooth'
						});
					}
				}
			},
			onMarkerMouseOut(location, index) {
				// unbind hover event
				this.hoveredListIndex = null;
				const items = document.querySelectorAll('.c-store-locations-listing__item');
				const target = items[index];
				if (target) {
					target.classList.remove('is-hovered');
				}
			},
			setMarkerRef(index) {
				return (el) => {
					this.markerRefs[index] = el;
				};
			},
			onListItemMouseOver(index) {
				// animate marker when item is hovered
				this.hoveredListIndex = index;
				const markerComponent = this.markerRefs[index];

				if (markerComponent && markerComponent.marker) {
					const marker = markerComponent.marker;
					if (marker.getAnimation() === null) {
						marker.setAnimation(window.google.maps.Animation.BOUNCE);
					}
				}
			},
			onListItemMouseOut(index) {
				// unbind hover event
				this.hoveredListIndex = null;
				const markerComponent = this.markerRefs[index];
				if (markerComponent && markerComponent.marker) {
					const marker = markerComponent.marker;
					if (marker.getAnimation() !== null) {
						marker.setAnimation(null);
					}
				}
			},
			resetSearch() {
				this.searchText = '';
			}
		},
	};
</script>

